/**
 * Authentication Store
 *
 * Manages session state (tokens, user) and auth flow state (loading, errors).
 * Implements automatic token refresh before expiration (5 minutes buffer).
 *
 * Token Architecture:
 * - Auth0 tokens stored in `accessToken`, sent as Authorization: Bearer header
 * - Legacy tokens stored in `legacyAuth`, sent as A3S-Legacy-Authorization header
 *
 * Both token types can coexist. The backend decides which to use for authentication.
 */
import { create } from "zustand";
import { Platform } from "react-native";
import { secureStorage, SecureStorageKeys } from "@src/services/secure-storage";
import { appStorage, AppStorageKeys } from "@src/services/app-storage";
import * as AuthSession from "expo-auth-session";
import { TokenResponse, TokenResponseConfig, RefreshTokenRequestConfig } from "expo-auth-session";
import { jwtDecode } from "jwt-decode";
import { authorization } from "@src/config/runtime";
import { getEnvironment } from "@src/config/environment-store";
import { trackEvent, resetAmplitude } from "@src/services/analytics/amplitude";
import { ANALYTICS_EVENTS } from "@src/services/analytics/events";
import type { User, Auth0TokenResponse, JWT, AuthState, AuthSessionTokenResponse, LegacyAuth } from "./types";
import { mergeUser } from "./user-profile-merge";
import { getUseLegacyAuth } from "@src/config/useLegacyAuth";
import { clearLastUsedTenant } from "@src/features/tenants";
import { useImpersonationStore } from "./impersonation-store";
import LegacyKitModule from "../../../modules/legacy-kit";

const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Tracks the in-flight refresh so authenticatedFetch can await it rather than
 * sending a stale token. Set when refreshToken() starts, cleared on completion.
 */
let refreshPromise: Promise<boolean> | null = null;

/** Returns the in-flight refresh promise, or null if no refresh is active. */
export function getRefreshPromise(): Promise<boolean> | null {
  return refreshPromise;
}

const persistAuthUser = (user: User): void => {
  appStorage.set(AppStorageKeys.AUTH_USER, user);
};

/** Native legacy keychain only; persists to secure storage when an email is found. */
const tryGetNativeLegacyEmail = (): string | undefined => {
  try {
    const legacyModule = LegacyKitModule as unknown as Record<string, unknown>;
    if (typeof legacyModule.getLegacyAuthData !== "function") return undefined;
    const email = (legacyModule.getLegacyAuthData() as { email?: string } | null)?.email?.trim();
    if (!email) return undefined;
    void secureStorage.set(SecureStorageKeys.legacyUserEmail, email);
    return email;
  } catch {
    return undefined;
  }
};

function parseJWT(token: string): User | null {
  try {
    const payload = jwtDecode<{
      sub?: string;
      email?: string;
      name?: string;
      picture?: string;
    }>(token);

    if (!payload.sub) return null;

    return {
      id: payload.sub,
      email: payload.email || "",
      name: payload.name,
      picture: payload.picture,
    };
  } catch {
    return null;
  }
}

function parseRolesFromAccessToken(accessToken: string): string[] {
  try {
    const payload = jwtDecode<{ "urn:visioni:roles"?: string[] }>(accessToken);
    return payload["urn:visioni:roles"] ?? [];
  } catch {
    return [];
  }
}

// TODO - Refactor this when there's clarity around legacy login and web.
// Check for existing session synchronously on web to prevent flash of sign-in screen
function getInitialAuthState(): {
  isAuthenticated: boolean;
  accessToken: string | null;
  legacyAuth: LegacyAuth | null;
  user: User | null;
} {
  if (Platform.OS === "web" && typeof window !== "undefined" && window.localStorage) {
    try {
      // Check for legacy tokens first (enables legacy login on web via /auth/login)
      const legacyTokenKey = `secure_${SecureStorageKeys.legacyAuthToken}`;
      const legacyUserIdKey = `secure_${SecureStorageKeys.legacyUserId}`;
      const legacyUserEmailKey = `secure_${SecureStorageKeys.legacyUserEmail}`;
      const legacyToken = window.localStorage.getItem(legacyTokenKey);
      const legacyUserId = window.localStorage.getItem(legacyUserIdKey);
      const legacyUserEmail = window.localStorage.getItem(legacyUserEmailKey);

      if (legacyToken && legacyUserId) {
        return {
          isAuthenticated: true,
          accessToken: null,
          legacyAuth: {
            token: legacyToken,
            userId: legacyUserId,
            userEmail: legacyUserEmail ?? undefined,
          },
          user: { id: legacyUserId, email: legacyUserEmail ?? "" },
        };
      }

      // Check for Auth0 JWT tokens
      const key = `secure_${SecureStorageKeys.jwtToken}`;
      const stored = window.localStorage.getItem(key);
      if (stored) {
        const jwt = JSON.parse(stored) as JWT;
        if (jwt?.accessToken) {
          const user = parseJWT(jwt.accessToken);
          if (user) {
            user.roles = parseRolesFromAccessToken(jwt.accessToken);
            return { isAuthenticated: true, accessToken: jwt.accessToken, legacyAuth: null, user };
          }
        }
      }
    } catch {
      // Ignore errors, will initialize properly later
    }
  }
  return { isAuthenticated: false, accessToken: null, legacyAuth: null, user: null };
}

const initialAuthState = getInitialAuthState();

export const useAuthStore = create<AuthState>()((set, get) => ({
  user: initialAuthState.user,
  accessToken: initialAuthState.accessToken,
  legacyAuth: initialAuthState.legacyAuth,
  isAuthenticated: initialAuthState.isAuthenticated,
  isInitialized: initialAuthState.isAuthenticated, // If already authenticated, consider initialized
  isAuthenticating: false,
  authError: null,
  isRefreshing: false,

  setAuthenticating: (isAuthenticating: boolean) => {
    set({ isAuthenticating });
  },

  setAuthError: (authError: string | null) => {
    set({ authError, isAuthenticating: false });
  },

  handleTokenExchange: async (tokenResponse: AuthSessionTokenResponse) => {
    try {
      if (!tokenResponse.accessToken) {
        throw new Error("No access token received");
      }

      const auth0Response = tokenResponseToAuth0Response(tokenResponse);
      await get().setAuth(auth0Response);
    } catch (error) {
      console.error("[AuthStore] Token exchange error:", error);
      set({
        authError: error instanceof Error ? error.message : "Authentication failed",
        isAuthenticating: false,
      });
      throw error;
    }
  },

  initialize: async () => {
    try {
      // Check legacy tokens FIRST (before checking sync state)
      // This ensures we check the correct token type for legacy auth flow
      const [legacyToken, legacyUserId, legacyUserEmail] = await Promise.all([
        secureStorage.get(SecureStorageKeys.legacyAuthToken),
        secureStorage.get(SecureStorageKeys.legacyUserId),
        secureStorage.get(SecureStorageKeys.legacyUserEmail),
      ]);
      const cachedUser = appStorage.get<User>(AppStorageKeys.AUTH_USER);

      if (legacyToken && legacyUserId) {
        let user: User = {
          id: legacyUserId,
          email: legacyUserEmail?.trim() ?? "",
        };
        user = mergeUser(user, cachedUser) ?? user;
        if (!user.email) {
          const nativeEmail = tryGetNativeLegacyEmail();
          if (nativeEmail) {
            user = mergeUser({ ...user, email: nativeEmail }, cachedUser) ?? user;
          }
        }

        // Legacy (Django token) sessions have no `urn:visioni:roles` claim and
        // no access token to parse roles from. Force roles to empty so a stale
        // `a3s:support` role cached from a prior Auth0 session (which mergeUser
        // would otherwise carry over via AUTH_USER) can't leak support access
        // into a legacy session that has no current token backing it. Strip
        // before persisting so the cached copy is sanitised too.
        user = { ...user, roles: [] };

        if (user.email) persistAuthUser(user);

        set({
          user,
          legacyAuth: {
            token: legacyToken,
            userId: legacyUserId,
            userEmail: user.email || legacyUserEmail || undefined,
          },
          isAuthenticated: true,
          isInitialized: true,
        });

        return;
      }

      // If already authenticated from sync check (web), just refresh token in background
      // Only do this for Auth0 flow (not legacy auth)
      if (get().isAuthenticated && !getUseLegacyAuth()) {
        set({ isInitialized: true });
        get().refreshToken();
        return;
      }

      // Auth0 JWT tokens flow (default)
      const jwt = await secureStorage.getJSON<JWT>(SecureStorageKeys.jwtToken);

      if (jwt?.accessToken) {
        const parsed = parseJWT(jwt.accessToken);

        if (parsed) {
          const user = mergeUser(parsed, cachedUser) ?? parsed;
          user.roles = parseRolesFromAccessToken(jwt.accessToken);
          persistAuthUser(user);

          set({
            user,
            accessToken: jwt.accessToken,
            isAuthenticated: true,
            isInitialized: true,
          });

          // Refresh token in background - tenant fetching is now handled by TenantProvider
          get().refreshToken();
          return;
        }
      }

      // No authentication tokens found - user needs to login
      set({ isInitialized: true });
    } catch (error) {
      console.error("[AuthStore] Initialization error:", error);
      set({ isInitialized: true });
    }
  },

  setAuth: async (tokens: Auth0TokenResponse, providedUser?: User | null) => {
    try {
      const jwt = toJWT(tokens);
      await secureStorage.setJSON(SecureStorageKeys.jwtToken, jwt);

      let user = providedUser;
      if (!user && tokens.id_token) {
        user = parseJWT(tokens.id_token);
      }

      if (user) {
        user = mergeUser(user, appStorage.get<User>(AppStorageKeys.AUTH_USER)) ?? user;
        user.roles = parseRolesFromAccessToken(tokens.access_token);
        persistAuthUser(user);
      }

      // Get current state to check for existing legacy token
      const currentState = get();
      const existingLegacyAuth = currentState.legacyAuth;

      // Set auth state but keep isAuthenticating true until tenant fetch completes
      set({
        user: user ?? null,
        accessToken: tokens.access_token,
        isAuthenticated: true,
        isInitialized: true,
        isAuthenticating: true, // Keep loading state active
        authError: null,
        // Keep existing legacyAuth if it exists (don't overwrite it)
        legacyAuth: existingLegacyAuth,
      });

      if (user) {
        trackEvent(ANALYTICS_EVENTS.LOGIN_COMPLETED, {
          user_id: user.id,
          email: user.email,
        });
      }

      // Tenant fetching is now handled by TenantProvider via React Query
      set({ isAuthenticating: false });
    } catch (error) {
      console.error("[AuthStore] Failed to set auth:", error);
      set({ isAuthenticating: false });
      throw error;
    }
  },

  clearAuth: async () => {
    try {
      // Revoke refresh token before clearing (need to get it first)
      const jwt = await secureStorage.getJSON<JWT>(SecureStorageKeys.jwtToken);
      if (jwt?.refreshToken) {
        await revokeRefreshToken(jwt.refreshToken);
      }

      // Clear impersonation state so it doesn't leak to the next session.
      useImpersonationStore.getState().clearImpersonation();

      // Flip auth state first so tenant-dependent screens unmount before tenant teardown.
      set({
        user: null,
        accessToken: null,
        legacyAuth: null,
        isAuthenticated: false,
        authError: null,
        isRefreshing: false,
      });

      // Clear all secure storage (tokens, PIN, etc.)
      await secureStorage.clearAll();

      // Clear all app storage (user preferences, auth user, biometric preferences, etc.)
      appStorage.clear();

      // Always clear legacy app credentials/storage to avoid leaking sessions between modes
      try {
        const legacyModule = LegacyKitModule as unknown as Record<string, unknown>;
        if (legacyModule && typeof legacyModule.clearAllLegacyCredentials === "function") {
          legacyModule.clearAllLegacyCredentials();
        } else {
          console.warn("[AuthStore] clearAuth: LegacyKitModule not available, skipping native credential wipe");
        }
      } catch (error) {
        console.error("[AuthStore] Failed to clear legacy credentials:", error);
        // Don't throw - continue with logout even if legacy clearing fails
      }

      // Clear tenant preference
      await clearLastUsedTenant();

      // Clear React Query cache to prevent data leakage between users
      try {
        // Lazy require to avoid circular dependency with query-client
        const { clearQueryCache } = require("@src/features/query-client");
        await clearQueryCache();
      } catch (error) {
        console.error("[AuthStore] Failed to clear query cache:", error);
        // Don't throw - continue with logout even if cache clearing fails
      }

      trackEvent(ANALYTICS_EVENTS.LOGOUT);
      resetAmplitude();
    } catch (error) {
      console.error("[AuthStore] Failed to clear auth:", error);
      throw error;
    }
  },

  /**
   * Returns Auth0 JWT access token if available.
   */
  getAccessToken: async () => {
    const { accessToken } = get();

    if (accessToken) {
      return accessToken;
    }

    // Try to load from secure storage if not in state
    const jwt = await secureStorage.getJSON<JWT>(SecureStorageKeys.jwtToken);
    if (jwt?.accessToken) {
      set({ accessToken: jwt.accessToken });
      return jwt.accessToken;
    }

    return null;
  },

  /**
   * Returns legacy authentication data if available.
   */
  getLegacyAuth: async () => {
    const { legacyAuth } = get();

    if (legacyAuth) {
      return legacyAuth;
    }

    // Try to load from secure storage if not in state
    const [token, userId, userEmail] = await Promise.all([
      secureStorage.get(SecureStorageKeys.legacyAuthToken),
      secureStorage.get(SecureStorageKeys.legacyUserId),
      secureStorage.get(SecureStorageKeys.legacyUserEmail),
    ]);

    if (token && userId) {
      const legacyAuthData: LegacyAuth = {
        token,
        userId,
        userEmail: userEmail ?? undefined,
      };
      set({ legacyAuth: legacyAuthData });
      return legacyAuthData;
    }

    return null;
  },

  refreshToken: async (options?: { force?: boolean }) => {
    // If a refresh is already in flight, coalesce onto it
    if (refreshPromise) {
      return refreshPromise;
    }

    const doRefresh = async (): Promise<boolean> => {
      try {
        const jwt = await secureStorage.getJSON<JWT>(SecureStorageKeys.jwtToken);

        if (!jwt?.refreshToken) {
          return false;
        }

        if (!options?.force) {
          const issuedAt = jwt.issuedAt || Date.now();
          const expiresAt = issuedAt + jwt.expiresIn * 1000;
          const shouldRefresh = Date.now() >= expiresAt - TOKEN_REFRESH_BUFFER_MS;

          if (!shouldRefresh) {
            return true;
          }
        }

        // Only signal isRefreshing once we know a network call is needed.
        // This avoids disrupting useAuthReady-gated queries on no-op checks.
        set({ isRefreshing: true });

        const env = await getEnvironment();
        const auth = authorization(env);

        const tokenConfig: TokenResponseConfig = {
          accessToken: jwt.accessToken,
          refreshToken: jwt.refreshToken,
          expiresIn: jwt.expiresIn,
          issuedAt: jwt.issuedAt || Date.now(),
          tokenType: "bearer",
          scope: jwt.scope,
        };

        let tokenResponse = new TokenResponse(tokenConfig);

        const refreshConfig: RefreshTokenRequestConfig = {
          clientId: auth.clientId,
          refreshToken: jwt.refreshToken,
        };

        const endpointConfig: Pick<AuthSession.DiscoveryDocument, "tokenEndpoint"> = {
          tokenEndpoint: auth.discovery.tokenEndpoint,
        };

        tokenResponse = await tokenResponse.refreshAsync(refreshConfig, endpointConfig);

        const newJwt: JWT = {
          accessToken: tokenResponse.accessToken,
          refreshToken: tokenResponse.refreshToken || jwt.refreshToken,
          expiresIn: tokenResponse.expiresIn || jwt.expiresIn,
          issuedAt: Date.now(),
          scope: tokenResponse.scope || jwt.scope,
        };

        await secureStorage.setJSON(SecureStorageKeys.jwtToken, newJwt);

        const currentUser = get().user;
        const user = mergeUser(parseJWT(tokenResponse.accessToken), currentUser) ?? currentUser;
        if (user) {
          user.roles = parseRolesFromAccessToken(tokenResponse.accessToken);
          persistAuthUser(user);
        }

        set({
          accessToken: tokenResponse.accessToken,
          user,
          isAuthenticated: true,
          isRefreshing: false,
        });

        return true;
      } catch (error) {
        console.error("[AuthStore] Failed to refresh token:", error);
        await get().clearAuth();
        return false;
      } finally {
        refreshPromise = null;
      }
    };

    refreshPromise = doRefresh();
    return refreshPromise;
  },
}));

function toJWT(tokens: Auth0TokenResponse): JWT {
  return {
    accessToken: tokens.access_token,
    refreshToken: tokens.refresh_token,
    expiresIn: tokens.expires_in,
    issuedAt: Date.now(),
    scope: tokens.scope || "openid profile email offline_access",
  };
}

function tokenResponseToAuth0Response(tokenResponse: AuthSessionTokenResponse): Auth0TokenResponse {
  return {
    access_token: tokenResponse.accessToken,
    refresh_token: tokenResponse.refreshToken || "",
    id_token: tokenResponse.idToken || "",
    token_type: tokenResponse.tokenType || "Bearer",
    expires_in: tokenResponse.expiresIn || 0,
    scope: tokenResponse.scope || "",
  };
}

async function revokeRefreshToken(refreshToken: string): Promise<void> {
  try {
    const env = await getEnvironment();
    const auth = authorization(env);

    const response = await fetch(`https://${auth.domain}/oauth/revoke`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        client_id: auth.clientId,
        token: refreshToken,
      }),
    });

    if (!response.ok) {
      console.warn("[AuthStore] Token revocation failed:", response.status);
    }
  } catch (error) {
    console.warn("[AuthStore] Token revocation error:", error);
  }
}

/** Rehydrate auth from storage after legacy→RN migration (boot or foreground). */
export async function reinitializeAuthAfterLegacyMigration(): Promise<void> {
  const store = useAuthStore.getState();
  await store.initialize();
}

/**
 * Canonical selector: true when the session has a valid token ready for API use.
 * Queries should gate on this rather than checking isAuthenticated alone.
 */
export function useAuthReady(): boolean {
  return useAuthStore((s) => s.isAuthenticated && s.isInitialized && !s.isRefreshing);
}
